/**
 * Shared device → icon and display label mapping used across the app.
 */

// Device display name mapping (full DB name → short tag label)
const DEVICE_DISPLAY_NAMES: Record<string, string> = {
  'D3.1 WIRED EMTA': 'D3.1 eMTA',
  'D3.1 2.5G WIRED EMTA': 'D3.1 eMTA',
  'D3.1 SB EMTA': 'D3.1 SB eMTA',
  'D4.0 EMTA': 'D4.0 eMTA',
  'WAVE 2 EMTA': 'Wave 2 eMTA',
  'D3 24X8 WIRED EMTA': 'D3.1 eMTA',
  'WIFI 6 ROUTER RES': 'WiFi 6',
  'WIFI 6 ROUTER SCP': 'WiFi 6',
  'WIFI 6E ROUTER RES': 'WiFi 6e',
  'WIFI 7 ROUTER RES': 'WiFi 7',
  'WIFI 7 ROUTER SCP': 'WiFi 7',
  'WAVE 2 ROUTER RES': 'Wave 2',
  'WAVE 2 ROUTER SCP': 'Wave 2',
  'XUMO STREAM BOX': 'Xumo',
  'IP STB': 'Video STB',
  'IP STP': 'Video STB',
  'APPLE TV': 'Apple TV',
  'WORLD BOX': 'WorldBox',
  'NON WORLD BOX': 'NonWorldBox',
  BBS: 'Backup WiFi',
  'WIFI 7 CELLULAR BACKUP ROUTER': 'Backup WiFi',
  '8HR BBU': '8hr BBU',
  '24HR BBU': '24hr BBU',
  'BACKUP WIFI': 'Backup WiFi',
  'WIFI 7 EXTENDER': 'WiFi 7 Extender',
  BILINGUAL: 'Bilingual',
};

// Explicit device name → icon lookup
const DEVICE_ICONS: Record<string, string> = {
  // Routers (eMTA variants) → ki-modem-f
  'D3.1 EMTA': 'ki-modem-f',
  'D3.1 WIRED EMTA': 'ki-modem-f',
  'D3.1 2.5G WIRED EMTA': 'ki-modem-f',
  'D3.1 SB EMTA': 'ki-modem-f',
  'D4.0 EMTA': 'ki-modem-f',
  'WAVE 2 EMTA': 'ki-modem-f',
  'D3 24X8 WIRED EMTA': 'ki-modem-f',
  // WiFi routers → ki-wifi-f
  'WIFI 6 ROUTER RES': 'ki-wifi-f',
  'WIFI 6 ROUTER SCP': 'ki-wifi-f',
  'WIFI 6E ROUTER RES': 'ki-wifi-f',
  'WIFI 7 ROUTER RES': 'ki-wifi-f',
  'WIFI 7 ROUTER SCP': 'ki-wifi-f',
  'WAVE 2 ROUTER RES': 'ki-wifi-f',
  'WAVE 2 ROUTER SCP': 'ki-wifi-f',
  // Xumo → ki-xumo-f
  'XUMO STREAM BOX': 'ki-xumo-f',
  // Video STB → ki-set-top-box-f
  'IP STB': 'ki-set-top-box-f',
  'IP STP': 'ki-set-top-box-f',
  // Apple TV → ki-apple
  'APPLE TV': 'ki-apple',
  // World Box / Non-World Box → ki-live-tv-f
  'WORLD BOX': 'ki-live-tv-f',
  'NON WORLD BOX': 'ki-live-tv-f',
  // Invincible WiFi / Backup / BBU → ki-battery-wifi-7
  BBS: 'ki-battery-wifi-7',
  'WIFI 7 CELLULAR BACKUP ROUTER': 'ki-battery-wifi-7',
  '8HR BBU': 'ki-battery-wifi-7',
  '24HR BBU': 'ki-battery-wifi-7',
  'BACKUP WIFI': 'ki-battery-wifi-7',
  // Extender → ki-network-f
  'WIFI 7 EXTENDER': 'ki-network-f',
  // Bilingual → ki-chat-support
  BILINGUAL: 'ki-chat-support',
};

/**
 * Returns the Kite icon name for a given device string.
 */
export const getDeviceIcon = (deviceName: string): string | undefined => {
  const mapped = DEVICE_ICONS[deviceName.toUpperCase()];
  if (mapped) return mapped;

  const name = deviceName.toLowerCase();

  if (name.includes('extender')) return 'ki-network-f';
  if (name.includes('bilingual')) return 'ki-chat-support';
  if (name.includes('backup') || name.includes('bbu') || name.includes('invincible'))
    return 'ki-battery-wifi-7';
  if (name.includes('apple')) return 'ki-apple';
  if (name.includes('world box')) return 'ki-live-tv-f';
  if (name.includes('stb') || name.includes('stp') || name.includes('set-top') || name.includes('set top'))
    return 'ki-set-top-box-f';
  if (name.includes('xumo') || name.includes('stream')) return 'ki-xumo-f';
  if (name.includes('wifi') || name.includes('wi-fi')) return 'ki-wifi-f';
  if (name.includes('emta') || name.includes('modem') || name.includes('d3.') || name.includes('d4.'))
    return 'ki-modem-f';

  return undefined;
};

/**
 * Returns the short display label for a device name (for tag rendering).
 */
export const getDeviceDisplayName = (deviceName: string): string => {
  return DEVICE_DISPLAY_NAMES[deviceName.toUpperCase()] || deviceName;
};

// Line-of-business icon lookup
const LOB_ICONS: Record<string, string> = {
  Residential: 'ki-home-f',
  Business: 'ki-office-f',
};

/**
 * Returns the Kite icon name for a line of business display value.
 */
export const getLobIcon = (lineOfBusiness: string): string => {
  return LOB_ICONS[lineOfBusiness] || 'ki-home-f';
};
